import { useCallback } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { signOut } from "../lib/auth";
import { setAuthTransition } from "../lib/auth-transition";
import { isPrivateQueryKey } from "../lib/identity-scope";
import { clearSpentRooms } from "../lib/spent-rooms";
import { useSession } from "./SessionProvider";

/**
 * The one way a player leaves — design spec §14.1 **ruling C, LOCKED**.
 *
 * > `?next=` records interruptions the system imposed (guard bounces, expired
 * > sessions, the §14.1-A conversion handoff). It never records a departure the
 * > player chose.
 *
 * So every user-initiated log-out lands on a **bare `/login`**, guest and
 * registered alike. Two things have to agree for that to be true, because both
 * of them fire:
 *
 *  1. this `navigate`, which is bare; and
 *  2. the route guard on the screen being left, which re-renders the instant
 *     `reload()` returns a null session and would otherwise capture the current
 *     path as a deep link. The `logging-out` transition is what makes it drop
 *     `?next=` instead — see `RequireStage`.
 *
 * Note the deliberate asymmetry with the conversion handoff, which signs the
 * player out and **keeps** its `?next=`: that sign-out is the system's doing,
 * mid-flow, and the whole promise of §14.1 ruling A is that it comes back.
 *
 * Not used by `/verify`'s "Use a different email", which also signs out: that
 * is a continuation of the sign-up the player is still in the middle of, not a
 * departure, so it keeps the deep link it was carrying.
 */
export function useLogout(): () => Promise<void> {
  const navigate = useNavigate();
  const { reload, forgetIdentity } = useSession();
  const queryClient = useQueryClient();

  return useCallback(async () => {
    /**
     * **SEC-2 (T-910), step 1 — this player's generation is not held past the
     * log out.** The generation is sticky across a signed-out read so the
     * conversion handoff keeps its state; a player-chosen log out is the one
     * departure that must not, so the next signed-out read resolves to
     * *nobody's* generation. It changes nothing until that read lands (the
     * profile still names this player), so it sits ahead of the LOCKED flag
     * choreography below rather than inside it.
     */
    forgetIdentity();
    setAuthTransition("logging-out");
    /**
     * The spent-room set is **session-scoped and identity-keyed** (lobby §6.7):
     * it is cleared on log out and whenever the identity changes. Clearing it
     * here as well as keying it by `players.id` is belt and braces on the one
     * failure mode that could manufacture a false entry — a second account in
     * the same tab inheriting the first one's history and finding a joinable
     * table greyed out with "you left this one".
     */
    clearSpentRooms();
    await signOut().catch(() => undefined);
    await reload();
    void navigate("/login", { replace: true });
    /**
     * The flag is **not** lowered here. React Router commits a navigation as a
     * transition and this fetch's `setState` at default priority, so React
     * renders the signed-out session against the *old* location first: lower it
     * on this line and the guard on the screen being left fires one render too
     * early, with `logging-out` already gone, and appends the very `?next=` the
     * ruling says to drop. `RequireAnonymous` lowers it on arrival instead, and
     * `SessionProvider` lowers it if the sign-out did not take.
     */
    /**
     * **SEC-2 (T-910), step 2 — the account's private state leaves with it.**
     * After the choreography above, untouched: `reload()` has already moved
     * every mounted private observer off this player's keys. **Cancel, then
     * remove** every private query, whatever generation it carries — cancel
     * first so a read this player started cannot land after the removal and
     * recreate the entry; remove rather than invalidate, because an
     * invalidated entry still HOLDS the rows and paints them. Not
     * `queryClient.clear()`: public entries (the shop catalog) are nobody's
     * and stay.
     */
    await queryClient.cancelQueries({ predicate: (query) => isPrivateQueryKey(query.queryKey) });
    queryClient.removeQueries({ predicate: (query) => isPrivateQueryKey(query.queryKey) });
  }, [navigate, reload, forgetIdentity, queryClient]);
}
